import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CheckCircle, Plus, XCircle } from 'lucide-react';
import { calcTemplateWeekHours, calcTemplateWeekDayHours } from '@/utils/labourHours';

export function UserDialog({ ctx }) {
  const {
    showCreateUser, setShowCreateUser, showEditUser, setShowEditUser,
    editingUser, setEditingUser, formData, setFormData, handleCreateUser,
    branches, shiftCodes, toggleBranch, toggleRole, systemRoles
  } = ctx;

  return (
    <Dialog open={showCreateUser || showEditUser} onOpenChange={(open) => {
      if (!open) { setShowCreateUser(false); setShowEditUser(false); setEditingUser(null); }
    }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editingUser ? 'Edit' : 'Create New'} User</DialogTitle></DialogHeader>
        <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>First Name</Label>
              <Input data-testid="user-firstname-input" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} placeholder="John" />
            </div>
            <div>
              <Label>Last Name</Label>
              <Input data-testid="user-lastname-input" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} placeholder="Smith" />
            </div>
          </div>
          <div>
            <Label>Username</Label>
            <Input data-testid="user-username-input" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required />
          </div>
          <div>
            <Label>Password {editingUser && '(leave blank to keep current)'}</Label>
            <Input data-testid="user-password-input" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!editingUser} />
          </div>
          <div>
            <Label>Role</Label>
            <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val })}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {formData.role === 'manager' && (
            <div>
              <Label>Assigned Branch</Label>
              <Select value={formData.branch} onValueChange={(val) => setFormData({ ...formData, branch: val })}>
                <SelectTrigger data-testid="manager-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{branches.map(b => (<SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          )}
          {formData.role === 'staff' && (
            <>
              <div>
                <Label>Branches (can work in)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {branches.map(b => (
                    <button key={b.id} type="button" data-testid={`branch-toggle-${b.name.toLowerCase()}`} onClick={() => toggleBranch(b.name)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition ${formData.branches.includes(b.name) ? 'bg-gray-600 text-white' : 'bg-stone-200 text-stone-700 hover:bg-stone-300'}`}>
                      {b.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Allowed Roles</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {shiftCodes.map(code => (
                    <button key={code} type="button" data-testid={`role-toggle-${code.toLowerCase()}`} onClick={() => toggleRole(code)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition ${formData.allowed_roles.includes(code) ? 'bg-gray-600 text-white' : 'bg-stone-200 text-stone-700 hover:bg-stone-300'}`}>
                      {code}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          <div>
            <Label>Email (Optional)</Label>
            <Input data-testid="user-email-input" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
          </div>
          <div>
            <Label>Employee Code (SimplePay ID)</Label>
            <Input data-testid="user-employee-code-input" value={formData.employee_code || ''} onChange={(e) => setFormData({ ...formData, employee_code: e.target.value })} placeholder="e.g., EMP001" />
            <p className="text-xs text-stone-500 mt-1">Used to sync leave from SimplePay</p>
          </div>
          <div>
            <Label>TimeTec User ID</Label>
            <Input data-testid="user-timetec-user-id-input" value={formData.timetec_user_id || ''} onChange={(e) => setFormData({ ...formData, timetec_user_id: e.target.value })} placeholder="e.g., 1" />
            <p className="text-xs text-stone-500 mt-1">Biometric User_ID from TimeTec (differs from Employee Code)</p>
          </div>
          <div>
            <Label>Working days per week</Label>
            <Input
              data-testid="user-working-days-per-week-input"
              type="number"
              min="1"
              max="7"
              value={formData.working_days_per_week ?? 5}
              onChange={(e) => setFormData({ ...formData, working_days_per_week: parseInt(e.target.value || '5', 10) })}
            />
            <p className="text-xs text-stone-500 mt-1">Used for public-holiday normal hours fallback (≤5 days → 9h; ≥6 → 8h).</p>
          </div>
          <div>
            <Label>Hourly Rate (R / hour)</Label>
            <Input
              data-testid="user-hourly-rate-input"
              type="number"
              min="0"
              step="0.01"
              value={formData.hourly_rate ?? 0}
              onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value || '0') })}
              placeholder="e.g., 45.50" />
            <p className="text-xs text-stone-500 mt-1">
              Used for indicative pay on the staff attendance report. Normal x1, Overtime x1.5, Sunday x1.5, Public Holiday x2.
            </p>
          </div>
          {formData.role === 'staff' && (
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200">
              <div>
                <Label className="text-sm font-medium text-stone-800">Can Work Evenings</Label>
                <p className="text-xs text-stone-500">Allow scheduling for shifts ending after 18:00</p>
              </div>
              <button type="button" data-testid="user-can-work-evenings-toggle"
                onClick={() => setFormData({ ...formData, can_work_evenings: !formData.can_work_evenings })}
                className={`relative w-11 h-6 rounded-full transition-colors ${formData.can_work_evenings ? 'bg-emerald-600' : 'bg-stone-300'}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${formData.can_work_evenings ? 'translate-x-5' : ''}`} />
              </button>
            </div>
          )}
          <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
            <div>
              <Label className="text-sm font-medium text-stone-800">Include in Gratuities</Label>
              <p className="text-xs text-stone-500">
                Tip-eligible: this user&apos;s approved hours count toward the Cash Recon gratuity apportionment. Turn OFF to exclude a specific staff member from the tip pool.
              </p>
            </div>
            <button type="button" data-testid="user-include-in-gratuities-toggle"
              onClick={() => setFormData({ ...formData, include_in_gratuities: !formData.include_in_gratuities })}
              className={`relative w-11 h-6 rounded-full transition-colors ${formData.include_in_gratuities ? 'bg-amber-600' : 'bg-stone-300'}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${formData.include_in_gratuities ? 'translate-x-5' : ''}`} />
            </button>
          </div>
          <Button type="submit" data-testid="submit-user-button" className="w-full">{editingUser ? 'Update' : 'Create'} User</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function TemplateDialog({ ctx }) {
  const {
    showCreateTemplate, setShowCreateTemplate, showEditTemplate, setShowEditTemplate,
    editingTemplate, setEditingTemplate, templateForm, setTemplateForm,
    handleCreateTemplate, branches, systemRoles
  } = ctx;

  return (
    <Dialog open={showCreateTemplate || showEditTemplate} onOpenChange={(open) => {
      if (!open) { setShowCreateTemplate(false); setShowEditTemplate(false); setEditingTemplate(null); }
    }}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editingTemplate ? 'Edit' : 'Create'} Shift Template</DialogTitle></DialogHeader>
        <form onSubmit={handleCreateTemplate} className="space-y-4 mt-4">
          <div>
            <Label>Branch</Label>
            <Select value={templateForm.branch} onValueChange={(val) => setTemplateForm({ ...templateForm, branch: val })}>
              <SelectTrigger data-testid="template-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
              <SelectContent>{branches.map(b => (<SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Role</Label>
            <Select value={templateForm.role_name} onValueChange={(val) => {
              const selectedRole = systemRoles.find(r => r.role_name === val);
              setTemplateForm({ ...templateForm, role_name: val, shift_code: selectedRole?.code || templateForm.shift_code });
            }}>
              <SelectTrigger data-testid="template-role-select"><SelectValue placeholder="Select a role" /></SelectTrigger>
              <SelectContent>{systemRoles.map(role => (<SelectItem key={role.id} value={role.role_name}><span className="font-medium">{role.code}</span> - {role.role_name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Shift Code (auto-filled from role)</Label>
            <Input data-testid="template-shift-code-input" value={templateForm.shift_code} onChange={(e) => setTemplateForm({ ...templateForm, shift_code: e.target.value.toUpperCase() })} placeholder="Auto-filled from role" required maxLength={10} className="bg-stone-50" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Start Time</Label><Input type="time" data-testid="template-start-time-input" value={templateForm.start_time} onChange={(e) => setTemplateForm({ ...templateForm, start_time: e.target.value })} required /></div>
            <div><Label>End Time</Label><Input type="time" data-testid="template-end-time-input" value={templateForm.end_time} onChange={(e) => setTemplateForm({ ...templateForm, end_time: e.target.value })} required /></div>
          </div>
          <div>
            <Label>Lunch Break (minutes)</Label>
            <Input type="number" data-testid="template-lunch-input" value={templateForm.lunch_minutes} onChange={(e) => setTemplateForm({ ...templateForm, lunch_minutes: parseInt(e.target.value) || 0 })} min="0" max="120" placeholder="0" />
          </div>
          <div>
            <Label>Template Color</Label>
            <div className="flex gap-3 items-center">
              <Input type="color" value={templateForm.color} onChange={(e) => setTemplateForm({ ...templateForm, color: e.target.value })} className="w-20 h-10" />
              <Input type="text" value={templateForm.color} onChange={(e) => setTemplateForm({ ...templateForm, color: e.target.value })} placeholder="#808080" className="font-mono" />
            </div>
          </div>
          <Button type="submit" data-testid="submit-template-button" className="w-full">{editingTemplate ? 'Update' : 'Create'} Template</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CloneShiftDialog({ ctx }) {
  const { showCloneWeek, setShowCloneWeek, cloneForm, setCloneForm, handleCloneWeek, selectedWeek, getISOWeekNumber } = ctx;

  return (
    <Dialog open={showCloneWeek} onOpenChange={setShowCloneWeek}>
      <DialogContent>
        <DialogHeader><DialogTitle>Clone Shifts</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="bg-stone-50 rounded-lg p-3 text-sm">
            <p className="font-medium text-stone-800">Source: Week {getISOWeekNumber(selectedWeek)}</p>
            <p className="text-stone-600">{new Date(selectedWeek).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
          </div>
          <div>
            <Label>Target Week Start Date</Label>
            <Input type="date" data-testid="clone-target-date" value={cloneForm.targetWeek} onChange={(e) => setCloneForm({ ...cloneForm, targetWeek: e.target.value })} />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="clone-assignments" checked={cloneForm.cloneAssignments} onChange={(e) => setCloneForm({ ...cloneForm, cloneAssignments: e.target.checked })} className="w-4 h-4 rounded" />
            <label htmlFor="clone-assignments" className="text-sm text-stone-700">Copy staff assignments</label>
          </div>
          <Button onClick={handleCloneWeek} data-testid="clone-week-submit" className="w-full" disabled={!cloneForm.targetWeek}>Clone Week</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CreateShiftFromTemplateDialog({ ctx }) {
  const {
    showCreateShift, setShowCreateShift, shiftForm, setShiftForm,
    shiftTemplates, getWeekDates, handleCreateShifts
  } = ctx;

  return (
    <Dialog open={showCreateShift} onOpenChange={setShowCreateShift}>
      <DialogContent>
        <DialogHeader><DialogTitle>Create Shifts from Template</DialogTitle></DialogHeader>
        <form onSubmit={handleCreateShifts} className="space-y-4 mt-4">
          <div>
            <Label>Select Templates (click to toggle)</Label>
            <div className="space-y-2 mt-2 max-h-48 overflow-y-auto">
              {shiftTemplates.filter(t => t.enabled).map(template => {
                const isSelected = shiftForm.template_ids.includes(template.id);
                return (
                  <div key={template.id} onClick={() => setShiftForm(prev => ({
                    ...prev, template_ids: isSelected ? prev.template_ids.filter(id => id !== template.id) : [...prev.template_ids, template.id]
                  }))} className={`p-3 border rounded-lg cursor-pointer transition ${isSelected ? 'border-gray-600 bg-gray-50' : 'border-stone-200 hover:border-stone-300'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'border-gray-600 bg-gray-600' : 'border-stone-300'}`}>
                        {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold text-stone-800">{template.shift_code}</span>
                        <span className="text-stone-600 text-sm ml-2">{template.role_name}</span>
                        <span className="text-stone-500 text-sm ml-2">({template.start_time}-{template.end_time})</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <Label>Select Dates (click to toggle)</Label>
            <div className="grid grid-cols-7 gap-2 mt-2">
              {getWeekDates().map((date, idx) => {
                const dayName = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][idx];
                const isSelected = shiftForm.dates.includes(date);
                return (
                  <button key={date} type="button" data-testid={`date-toggle-${date}`}
                    onClick={() => setShiftForm(prev => ({ ...prev, dates: isSelected ? prev.dates.filter(d => d !== date) : [...prev.dates, date] }))}
                    className={`p-2 rounded text-xs font-medium transition ${isSelected ? 'bg-gray-600 text-white' : 'bg-stone-200 text-stone-700 hover:bg-stone-300'}`}>
                    <div>{dayName}</div><div className="text-xs">{new Date(date).getDate()}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <Button type="submit" data-testid="submit-shift-button" className="w-full" disabled={shiftForm.template_ids.length === 0 || shiftForm.dates.length === 0}>
            Create {shiftForm.template_ids.length * shiftForm.dates.length} Shift{(shiftForm.template_ids.length * shiftForm.dates.length) !== 1 ? 's' : ''}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function DirectCreateShiftDialog({ ctx }) {
  const {
    showCreateDirectShift, setShowCreateDirectShift, directShiftForm, setDirectShiftForm,
    handleCreateDirectShifts, systemRoles, getWeekDates
  } = ctx;

  return (
    <Dialog open={showCreateDirectShift} onOpenChange={setShowCreateDirectShift}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Create Shift</DialogTitle></DialogHeader>
        <form onSubmit={handleCreateDirectShifts} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Shift Code</Label>
              <Select value={directShiftForm.shift_code} onValueChange={(val) => setDirectShiftForm(prev => ({ ...prev, shift_code: val }))}>
                <SelectTrigger data-testid="direct-shift-code-select"><SelectValue placeholder="Select code" /></SelectTrigger>
                <SelectContent>{systemRoles.map(role => (<SelectItem key={role.code} value={role.code}>{role.code} - {role.role_name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Max Staff Capacity</Label>
              <Input type="number" min="1" max="20" value={directShiftForm.max_capacity} data-testid="direct-shift-capacity" onChange={(e) => setDirectShiftForm(prev => ({ ...prev, max_capacity: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><Label>Start Time</Label><Input type="time" value={directShiftForm.start_time} data-testid="direct-shift-start" onChange={(e) => setDirectShiftForm(prev => ({ ...prev, start_time: e.target.value }))} /></div>
            <div><Label>End Time</Label><Input type="time" value={directShiftForm.end_time} data-testid="direct-shift-end" onChange={(e) => setDirectShiftForm(prev => ({ ...prev, end_time: e.target.value }))} /></div>
            <div><Label>Lunch (min)</Label><Input type="number" min="0" max="120" value={directShiftForm.lunch_minutes} data-testid="direct-shift-lunch" onChange={(e) => setDirectShiftForm(prev => ({ ...prev, lunch_minutes: e.target.value }))} /></div>
          </div>
          <div>
            <Label>Select Dates</Label>
            <div className="grid grid-cols-7 gap-2 mt-2">
              {getWeekDates().map((date, idx) => {
                const dayName = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][idx];
                const isSelected = directShiftForm.dates.includes(date);
                return (
                  <button key={date} type="button" data-testid={`direct-date-toggle-${date}`}
                    onClick={() => setDirectShiftForm(prev => ({ ...prev, dates: isSelected ? prev.dates.filter(d => d !== date) : [...prev.dates, date] }))}
                    className={`p-2 rounded text-xs font-medium transition ${isSelected ? 'bg-gray-600 text-white' : 'bg-stone-200 text-stone-700 hover:bg-stone-300'}`}>
                    <div>{dayName}</div><div className="text-xs">{new Date(date).getDate()}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <Button type="submit" data-testid="submit-direct-shift-button" className="w-full" disabled={!directShiftForm.shift_code || directShiftForm.dates.length === 0}>
            Create {directShiftForm.dates.length} Shift{directShiftForm.dates.length !== 1 ? 's' : ''}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AssignShiftDialog({ ctx }) {
  const {
    showAssignShift, setShowAssignShift, assigningShift, selectedStaffForShift,
    setSelectedStaffForShift, staffList, overrideHoursLimit, setOverrideHoursLimit,
    overrideUnavailability, setOverrideUnavailability, handleAssignShiftMultiple,
    unavailability
  } = ctx;

  const toggleStaff = (staffId) => {
    setSelectedStaffForShift(prev => prev.includes(staffId) ? prev.filter(id => id !== staffId) : [...prev, staffId]);
  };

  // Check which staff have unavailability on the shift date
  const getUnavailReason = (staffId) => {
    if (!assigningShift || !unavailability) return null;
    const u = unavailability.find(ua => ua.user_id === staffId && ua.date_from <= assigningShift.date && ua.date_to >= assigningShift.date);
    return u ? (u.hours ? `${u.reason} (${u.hours}h)` : u.reason) : null;
  };

  return (
    <Dialog open={showAssignShift} onOpenChange={(open) => {
      setShowAssignShift(open);
      if (!open) { setSelectedStaffForShift([]); setOverrideHoursLimit(false); setOverrideUnavailability(false); }
    }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Assign Shift to Staff</DialogTitle></DialogHeader>
        {assigningShift && (
          <div className="space-y-4 mt-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-sm font-semibold text-stone-800">{assigningShift.shift_code} - {assigningShift.role_name}</div>
              <div className="text-xs text-stone-600 mt-1">{assigningShift.branch}</div>
              <div className="text-xs text-stone-600">{new Date(assigningShift.date).toLocaleDateString()}</div>
              <div className="text-xs text-stone-600">{assigningShift.start_time} - {assigningShift.end_time}</div>
              <div className="text-xs font-semibold text-stone-700 mt-1">
                Capacity: {assigningShift.assigned_users?.length || 0}/{assigningShift.max_capacity || 1}
              </div>
              {assigningShift.assigned_users?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="text-xs font-medium text-stone-700">Currently Assigned:</div>
                  <div className="text-xs text-stone-600">{assigningShift.assigned_users.map(u => u.username).join(', ')}</div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 p-2.5 bg-orange-50 border border-orange-200 rounded-lg">
                <input type="checkbox" id="override-hours" checked={overrideHoursLimit} onChange={(e) => setOverrideHoursLimit(e.target.checked)} className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500" />
                <label htmlFor="override-hours" className="text-sm text-stone-700 cursor-pointer">Override 45-hour weekly limit</label>
              </div>
              <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                <input type="checkbox" id="override-unavail" checked={overrideUnavailability} onChange={(e) => setOverrideUnavailability(e.target.checked)} className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500" />
                <label htmlFor="override-unavail" className="text-sm text-stone-700 cursor-pointer">Override unavailability</label>
              </div>
            </div>
            <div>
              <Label>Select Staff Members (multiple allowed)</Label>
              {staffList.length > 0 ? (
                <div className="space-y-2 mt-2 max-h-60 overflow-y-auto">
                  {staffList.map(staff => {
                    const isSelected = selectedStaffForShift.includes(staff.id);
                    const unavailReason = getUnavailReason(staff.id);
                    const displayName = staff.first_name && staff.last_name ? `${staff.first_name} ${staff.last_name} (${staff.username})` : staff.username;
                    return (
                      <div key={staff.id} onClick={() => toggleStaff(staff.id)} data-testid={`assign-to-${staff.username}`}
                        className={`p-3 border rounded-lg cursor-pointer transition ${isSelected ? 'border-blue-500 bg-blue-50' : unavailReason ? 'border-amber-300 bg-amber-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                            {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                          </div>
                          <div className="flex-1">
                            <span className="text-sm">{displayName}</span>
                            {unavailReason && (
                              <div className="text-[11px] text-amber-700 font-medium mt-0.5">Unavailable: {unavailReason}</div>
                            )}
                          </div>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${staff.weekly_hours >= 45 ? 'bg-red-100 text-red-700' : staff.weekly_hours >= 35 ? 'bg-orange-100 text-orange-700' : 'bg-stone-100 text-stone-600'}`}>
                            {staff.weekly_hours ?? 0}h
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="text-sm text-stone-500 mt-2">No eligible staff members available</p>}
            </div>
            {selectedStaffForShift.length > 0 && (
              <Button onClick={handleAssignShiftMultiple} data-testid="assign-multiple-btn" className="w-full bg-blue-600 hover:bg-blue-700">
                Assign {selectedStaffForShift.length} Staff Member{selectedStaffForShift.length > 1 ? 's' : ''}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function EditShiftDialog({ ctx }) {
  const {
    showEditShift, setShowEditShift, editingShift, setEditingShift,
    handleSaveShiftEdit, handleUnassignShift, handleUnassignUser,
    openAssignDialog, systemRoles
  } = ctx;

  return (
    <Dialog open={showEditShift} onOpenChange={setShowEditShift}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Edit Shift</DialogTitle></DialogHeader>
        {editingShift && (
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Shift Code</Label>
                <Select value={editingShift.shift_code} onValueChange={(val) => setEditingShift({ ...editingShift, shift_code: val })}>
                  <SelectTrigger data-testid="edit-shift-code"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(systemRoles || []).map(role => (
                      <SelectItem key={role.code} value={role.code}>{role.code} - {role.role_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={editingShift.date} data-testid="edit-shift-date" onChange={(e) => setEditingShift({ ...editingShift, date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Start Time</Label><Input type="time" value={editingShift.start_time} data-testid="edit-shift-start" onChange={(e) => setEditingShift({ ...editingShift, start_time: e.target.value })} /></div>
              <div><Label>End Time</Label><Input type="time" value={editingShift.end_time} data-testid="edit-shift-end" onChange={(e) => setEditingShift({ ...editingShift, end_time: e.target.value })} /></div>
              <div><Label>Lunch (min)</Label><Input type="number" min="0" max="120" value={editingShift.lunch_minutes || 0} data-testid="edit-shift-lunch" onChange={(e) => setEditingShift({ ...editingShift, lunch_minutes: parseInt(e.target.value) || 0 })} /></div>
            </div>
            <div>
              <Label>Max Capacity</Label>
              <Input type="number" min="1" max="20" value={editingShift.max_capacity || 1} data-testid="edit-shift-capacity" onChange={(e) => setEditingShift({ ...editingShift, max_capacity: parseInt(e.target.value) || 1 })} />
            </div>
            {editingShift.assigned_users && editingShift.assigned_users.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="text-xs font-medium text-green-800 mb-2">Assigned ({editingShift.assigned_users.length}/{editingShift.max_capacity || 1}):</div>
                <div className="space-y-1">
                  {editingShift.assigned_users.map(u => (
                    <div key={u.user_id} className="flex items-center justify-between bg-green-100 rounded px-2 py-1">
                      <span className="text-xs text-green-700">{u.username}</span>
                      <button onClick={() => handleUnassignUser(editingShift.id, u.user_id)} className="text-red-500 hover:text-red-700" data-testid={`edit-remove-${u.user_id}`}>
                        <XCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={handleSaveShiftEdit} className="flex-1" data-testid="save-shift-edit">Save Changes</Button>
              <Button onClick={() => openAssignDialog(editingShift)} variant="outline" className="flex-1" data-testid="add-more-staff-btn">
                <Plus className="w-4 h-4 mr-1" /> Assign Staff
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function UnavailabilityDialog({ ctx }) {
  const { showUnavailability, setShowUnavailability, handleAddUnavailability, users, user } = ctx;
  const [isPartialDay, setIsPartialDay] = React.useState(false);
  const [isRecurring, setIsRecurring] = React.useState(false);
  const [selectedDays, setSelectedDays] = React.useState([]);

  const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const toggleDay = (idx) => {
    setSelectedDays(prev => prev.includes(idx) ? prev.filter(d => d !== idx) : [...prev, idx].sort());
  };

  const resetForm = () => {
    setIsPartialDay(false);
    setIsRecurring(false);
    setSelectedDays([]);
  };

  return (
    <Dialog open={showUnavailability} onOpenChange={(open) => { setShowUnavailability(open); if (!open) resetForm(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Unavailability Period</DialogTitle></DialogHeader>
        <form onSubmit={(e) => {
          e.preventDefault();
          const fd = new FormData(e.target);
          const data = { user_id: fd.get('user_id') || user.id, date_from: fd.get('date_from'), date_to: fd.get('date_to'), reason: fd.get('reason') };
          if (isPartialDay && fd.get('hours')) data.hours = parseFloat(fd.get('hours'));
          if (isRecurring) {
            if (selectedDays.length === 0) { alert('Please select at least one day of the week'); return; }
            // Calculate how many entries will be created
            const start = new Date(data.date_from);
            const end = new Date(data.date_to);
            let count = 0;
            const d = new Date(start);
            while (d <= end) {
              const dow = (d.getDay() + 6) % 7; // JS Sun=0 -> Mon=0
              if (selectedDays.includes(dow)) count++;
              d.setDate(d.getDate() + 1);
            }
            if (count === 0) { alert('No matching days found in the selected date range. Make sure the End Date is far enough out.'); return; }
            if (!window.confirm(`This will create ${count} individual unavailability entries (${selectedDays.map(d => DAY_NAMES[d]).join(', ')}) from ${data.date_from} to ${data.date_to}.\n\nContinue?`)) return;
            data.is_recurring = true;
            data.days_of_week = selectedDays;
          }
          handleAddUnavailability(data);
        }} className="space-y-4 mt-4">
          {user.role === 'admin' && (
            <div>
              <Label>Staff Member</Label>
              <Select name="user_id" required>
                <SelectTrigger data-testid="unavail-user-select"><SelectValue placeholder="Select staff member" /></SelectTrigger>
                <SelectContent>{users.filter(u => u.role === 'staff' || u.role === 'manager').map(u => (<SelectItem key={u.id} value={u.id}>{u.username} ({u.role})</SelectItem>))}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{isRecurring ? 'Start Date' : 'From Date'}</Label><Input type="date" name="date_from" data-testid="unavail-date-from" required /></div>
            <div><Label>{isRecurring ? 'End Date' : 'To Date'}</Label><Input type="date" name="date_to" data-testid="unavail-date-to" required /></div>
          </div>
          <div><Label>Reason</Label><Input name="reason" placeholder="e.g., Vacation, Studies, Medical" data-testid="unavail-reason" required /></div>

          {/* Recurring toggle */}
          <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200">
            <div>
              <Label className="text-sm font-medium text-stone-800">Recurring</Label>
              <p className="text-xs text-stone-500">Repeat on specific days of the week</p>
            </div>
            <button type="button" data-testid="unavail-recurring-toggle"
              onClick={() => setIsRecurring(!isRecurring)}
              className={`relative w-11 h-6 rounded-full transition-colors ${isRecurring ? 'bg-violet-600' : 'bg-stone-300'}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${isRecurring ? 'translate-x-5' : ''}`} />
            </button>
          </div>
          {isRecurring && (
            <div>
              <Label>Repeat on</Label>
              <div className="flex gap-1.5 mt-1">
                {DAY_NAMES.map((name, idx) => (
                  <button key={idx} type="button" onClick={() => toggleDay(idx)} data-testid={`unavail-day-${idx}`}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${selectedDays.includes(idx) ? 'bg-violet-600 text-white' : 'bg-white border border-stone-300 text-stone-600 hover:border-violet-400'}`}>
                    {name}
                  </button>
                ))}
              </div>
              <p className="text-xs text-amber-600 font-medium mt-2">Set the End Date far enough out to cover all the weeks you need. Entries will be created for every selected day between Start and End date. You can delete individual entries later.</p>
            </div>
          )}

          {/* Partial day toggle */}
          <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200">
            <div>
              <Label className="text-sm font-medium text-stone-800">Partial Day</Label>
              <p className="text-xs text-stone-500">Set specific hours unavailable</p>
            </div>
            <button type="button" data-testid="unavail-partial-toggle"
              onClick={() => setIsPartialDay(!isPartialDay)}
              className={`relative w-11 h-6 rounded-full transition-colors ${isPartialDay ? 'bg-emerald-600' : 'bg-stone-300'}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${isPartialDay ? 'translate-x-5' : ''}`} />
            </button>
          </div>
          {isPartialDay && (
            <div>
              <Label>Hours Unavailable</Label>
              <Input type="number" name="hours" step="0.5" min="0.5" max="24" placeholder="e.g., 4" data-testid="unavail-hours" required />
              <p className="text-xs text-stone-500 mt-1">Number of hours unavailable (e.g., 4 for half day)</p>
            </div>
          )}
          <Button type="submit" className="w-full" data-testid="submit-unavailability">Add Unavailability</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RoleDialog({ ctx }) {
  const { showCreateRole, setShowCreateRole, handleCreateRole } = ctx;

  return (
    <Dialog open={showCreateRole} onOpenChange={setShowCreateRole}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add New Staff Role</DialogTitle></DialogHeader>
        <form onSubmit={(e) => {
          e.preventDefault();
          const fd = new FormData(e.target);
          handleCreateRole({ code: fd.get('code'), role_name: fd.get('role_name') });
        }} className="space-y-4 mt-4">
          <div><Label>Role Code</Label><Input name="code" placeholder="e.g., M, S, C, K" maxLength={5} data-testid="role-code-input" required /><p className="text-xs text-stone-500 mt-1">Maximum 5 characters</p></div>
          <div><Label>Role Name</Label><Input name="role_name" placeholder="e.g., Manager, Sales, Coffee" data-testid="role-name-input" required /></div>
          <Button type="submit" className="w-full" data-testid="submit-role">Create Role</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function BranchDialog({ ctx }) {
  const {
    showCreateBranch, setShowCreateBranch, showEditBranch, setShowEditBranch,
    editingBranch, setEditingBranch, branchForm, setBranchForm,
    createBranch, updateBranch
  } = ctx;

  return (
    <Dialog open={showCreateBranch || showEditBranch} onOpenChange={(open) => {
      if (!open) { setShowCreateBranch(false); setShowEditBranch(false); setEditingBranch(null); setBranchForm({ name: '', color: '#808080', avg_labour_cost_per_hour: 0, cash_manager_branch_name: '' }); }
    }}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editingBranch ? 'Edit Branch' : 'Create New Branch'}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); editingBranch ? updateBranch() : createBranch(); }} className="space-y-4">
          <div><Label>Branch Name</Label><Input data-testid="branch-name-input" value={branchForm.name} onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })} placeholder="e.g., Town, Mall, Bayview" required /></div>
          <div>
            <Label>Branch Color</Label>
            <div className="flex gap-3 items-center">
              <Input type="color" value={branchForm.color} onChange={(e) => setBranchForm({ ...branchForm, color: e.target.value })} className="w-20 h-10" />
              <Input type="text" value={branchForm.color} onChange={(e) => setBranchForm({ ...branchForm, color: e.target.value })} placeholder="#808080" className="font-mono" />
            </div>
          </div>
          <div>
            <Label>Avg. Labour Cost per Hour (R)</Label>
            <Input data-testid="branch-labour-cost-input" type="number" step="0.01" min="0" value={branchForm.avg_labour_cost_per_hour || ''} onChange={(e) => setBranchForm({ ...branchForm, avg_labour_cost_per_hour: parseFloat(e.target.value) || 0 })} placeholder="e.g., 55.00" />
            <p className="text-xs text-stone-500 mt-1">Used to estimate total labour cost on the schedule</p>
          </div>
          <div>
            <Label>Cash Manager Branch Name <span className="text-stone-400 font-normal">(optional)</span></Label>
            <Input
              data-testid="branch-cash-manager-name-input"
              value={branchForm.cash_manager_branch_name || ''}
              onChange={(e) => setBranchForm({ ...branchForm, cash_manager_branch_name: e.target.value })}
              placeholder="e.g., Brothers Coffee Dana Bay"
            />
            <p className="text-xs text-stone-500 mt-1">
              Only set this if the Cash Recon app uses a different name for this branch. Must match exactly.
              See <i>Gratuities tab → Unmatched branches</i> for the names the Cash Recon API is sending.
            </p>
          </div>
          <Button type="submit" className="w-full">{editingBranch ? 'Update Branch' : 'Create Branch'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


export function SaveWeekAsTemplateDialog({ ctx }) {
  const { showSaveWeekAsTemplate, setShowSaveWeekAsTemplate, handleSaveWeekAsTemplate, selectedBranch } = ctx;
  const [name, setName] = React.useState('');

  return (
    <Dialog open={showSaveWeekAsTemplate} onOpenChange={(open) => { setShowSaveWeekAsTemplate(open); if (!open) setName(''); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Save Current Week as Template</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <p className="text-sm text-stone-600">This will save all shifts from the currently viewed week ({selectedBranch}) as a reusable template.</p>
          <div>
            <Label>Template Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Standard Week, Holiday Week" data-testid="template-week-name-input" />
          </div>
          <Button onClick={() => { handleSaveWeekAsTemplate(name); setName(''); }} disabled={!name.trim()} className="w-full" data-testid="save-week-template-btn">
            Save as Template
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ApplyTemplateWeekDialog({ ctx }) {
  const { showApplyTemplateWeek, setShowApplyTemplateWeek, templateWeeks, selectedBranch, handleApplyTemplateWeek } = ctx;
  const [selectedTwId, setSelectedTwId] = React.useState('');
  const branchTemplates = (templateWeeks || []).filter(tw => tw.branch === selectedBranch);
  const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const selectedTw = branchTemplates.find(tw => tw.id === selectedTwId);

  return (
    <Dialog open={showApplyTemplateWeek} onOpenChange={(open) => { setShowApplyTemplateWeek(open); if (!open) setSelectedTwId(''); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Apply Template Week</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <p className="text-sm text-stone-600">Apply a template week to the current week for <span className="font-semibold">{selectedBranch}</span>.</p>
          {branchTemplates.length > 0 ? (
            <>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {branchTemplates.map(tw => (
                  <div key={tw.id} onClick={() => setSelectedTwId(tw.id)} data-testid={`select-tw-${tw.id}`}
                    className={`p-3 border rounded-lg cursor-pointer transition ${selectedTwId === tw.id ? 'border-gray-600 bg-gray-50 ring-1 ring-gray-600' : 'border-stone-200 hover:border-stone-300'}`}>
                    <div className="font-semibold text-stone-800">{tw.name}</div>
                    <div className="text-xs text-stone-500">{tw.shifts?.length || 0} shifts &middot; <span className="font-semibold">{calcTemplateWeekHours(tw.shifts)}h</span> labour</div>
                    {selectedTwId === tw.id && selectedTw && (
                      <div className="grid grid-cols-7 gap-1 mt-2">
                        {DAY_NAMES.map((day, idx) => {
                          const dayShifts = (selectedTw.shifts || []).filter(s => s.day_of_week === idx);
                          return (
                            <div key={idx} className="bg-white rounded p-1">
                              <div className="text-[9px] font-semibold text-stone-500">{day}</div>
                              {dayShifts.map((s, i) => (
                                <div key={i} className="text-[9px] text-stone-700">{s.shift_code} x{s.max_capacity}</div>
                              ))}
                              {dayShifts.length === 0 && <div className="text-[9px] text-stone-300">—</div>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <Button onClick={() => { handleApplyTemplateWeek(selectedTwId); setSelectedTwId(''); }} disabled={!selectedTwId} className="w-full" data-testid="apply-template-week-btn">
                Apply Template
              </Button>
            </>
          ) : (
            <div className="text-center py-6 text-stone-500">
              <p>No template weeks saved for {selectedBranch} yet.</p>
              <p className="text-xs mt-1">Go to Settings → Templates to create one.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CreateTemplateWeekDialog({ ctx }) {
  const { showCreateTemplateWeek, setShowCreateTemplateWeek, handleCreateTemplateWeek, selectedBranch, systemRoles } = ctx;
  const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const [name, setName] = React.useState('');
  const [shifts, setShifts] = React.useState([]);

  const addShift = (dayOfWeek) => {
    setShifts(prev => [...prev, { shift_code: systemRoles[0]?.code || 'S', start_time: '07:00', end_time: '15:00', lunch_minutes: 30, max_capacity: 1, day_of_week: dayOfWeek }]);
  };
  const removeShift = (idx) => setShifts(prev => prev.filter((_, i) => i !== idx));
  const updateShift = (idx, field, value) => setShifts(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));

  const handleSubmit = () => {
    handleCreateTemplateWeek({ name, shifts });
    setName(''); setShifts([]);
  };

  return (
    <Dialog open={showCreateTemplateWeek} onOpenChange={(open) => { setShowCreateTemplateWeek(open); if (!open) { setName(''); setShifts([]); } }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Create Template Week — {selectedBranch}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label>Template Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Standard Week" data-testid="tw-name-input" />
          </div>
          {shifts.length > 0 && (
            <div className="bg-stone-100 rounded-lg px-3 py-2 flex items-center justify-between" data-testid="tw-total-hours">
              <span className="text-sm font-semibold text-stone-700">Total Labour Hours</span>
              <span className="text-lg font-bold text-stone-800">{calcTemplateWeekHours(shifts)}h</span>
            </div>
          )}
          <div className="grid grid-cols-7 gap-1">
            {(() => { const dayHrs = calcTemplateWeekDayHours(shifts); return DAY_NAMES.map((day, dayIdx) => {
              const dayShifts = shifts.filter(s => s.day_of_week === dayIdx);
              return (
                <div key={dayIdx} className="border border-stone-200 rounded p-1.5 bg-stone-50 min-h-[100px]">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <span className="text-xs font-semibold text-stone-700">{day}</span>
                      {dayHrs[dayIdx] > 0 && <div className="text-[10px] font-medium text-emerald-700" data-testid={`tw-day-hours-${dayIdx}`}>{dayHrs[dayIdx]}h</div>}
                    </div>
                    <button onClick={() => addShift(dayIdx)} className="w-4 h-4 rounded-full bg-gray-600 text-white flex items-center justify-center hover:bg-gray-700 text-xs" data-testid={`add-shift-day-${dayIdx}`}>+</button>
                  </div>
                  <div className="space-y-1">
                    {shifts.map((s, idx) => s.day_of_week === dayIdx && (
                      <div key={idx} className="bg-white border border-stone-200 rounded p-1 text-[10px]">
                        <div className="flex items-center justify-between mb-0.5">
                          <select value={s.shift_code} onChange={(e) => updateShift(idx, 'shift_code', e.target.value)} className="text-[10px] bg-transparent font-semibold w-12 outline-none">
                            {systemRoles.map(r => <option key={r.code} value={r.code}>{r.code}</option>)}
                          </select>
                          <button onClick={() => removeShift(idx)} className="text-red-400 hover:text-red-600 text-xs">&times;</button>
                        </div>
                        <input type="time" value={s.start_time} onChange={(e) => updateShift(idx, 'start_time', e.target.value)} className="text-[10px] w-full bg-transparent outline-none" />
                        <input type="time" value={s.end_time} onChange={(e) => updateShift(idx, 'end_time', e.target.value)} className="text-[10px] w-full bg-transparent outline-none" />
                        <div className="flex gap-1 mt-0.5">
                          <input type="number" value={s.max_capacity} min="1" onChange={(e) => updateShift(idx, 'max_capacity', parseInt(e.target.value) || 1)} className="text-[10px] w-8 bg-transparent outline-none border-b border-stone-200" title="Capacity" />
                          <span className="text-stone-400">cap</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }); })()}
          </div>
          <Button onClick={handleSubmit} disabled={!name.trim() || shifts.length === 0} className="w-full" data-testid="create-tw-submit-btn">
            Create Template ({shifts.length} shifts &middot; {calcTemplateWeekHours(shifts)}h)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EditTemplateWeekDialog({ ctx }) {
  const { showEditTemplateWeek, setShowEditTemplateWeek, handleUpdateTemplateWeek, systemRoles } = ctx;
  const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const [name, setName] = React.useState('');
  const [shifts, setShifts] = React.useState([]);
  const tw = showEditTemplateWeek;

  React.useEffect(() => {
    if (tw && typeof tw === 'object') {
      setName(tw.name || '');
      setShifts((tw.shifts || []).map(s => ({ ...s })));
    }
  }, [tw]);

  const addShift = (dayOfWeek) => {
    setShifts(prev => [...prev, { shift_code: systemRoles[0]?.code || 'S', start_time: '07:00', end_time: '15:00', lunch_minutes: 30, max_capacity: 1, day_of_week: dayOfWeek }]);
  };
  const removeShift = (idx) => setShifts(prev => prev.filter((_, i) => i !== idx));
  const updateShift = (idx, field, value) => setShifts(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));

  if (!tw || typeof tw !== 'object') return null;

  return (
    <Dialog open={!!tw} onOpenChange={(open) => { if (!open) setShowEditTemplateWeek(null); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Template Week — {tw.branch}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label>Template Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-tw-name-input" />
          </div>
          {shifts.length > 0 && (
            <div className="bg-stone-100 rounded-lg px-3 py-2 flex items-center justify-between" data-testid="tw-edit-total-hours">
              <span className="text-sm font-semibold text-stone-700">Total Labour Hours</span>
              <span className="text-lg font-bold text-stone-800">{calcTemplateWeekHours(shifts)}h</span>
            </div>
          )}
          <div className="grid grid-cols-7 gap-1">
            {(() => { const dayHrs = calcTemplateWeekDayHours(shifts); return DAY_NAMES.map((day, dayIdx) => (
              <div key={dayIdx} className="border border-stone-200 rounded p-1.5 bg-stone-50 min-h-[100px]">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <span className="text-xs font-semibold text-stone-700">{day}</span>
                    {dayHrs[dayIdx] > 0 && <div className="text-[10px] font-medium text-emerald-700" data-testid={`tw-edit-day-hours-${dayIdx}`}>{dayHrs[dayIdx]}h</div>}
                  </div>
                  <button onClick={() => addShift(dayIdx)} className="w-4 h-4 rounded-full bg-gray-600 text-white flex items-center justify-center hover:bg-gray-700 text-xs">+</button>
                </div>
                <div className="space-y-1">
                  {shifts.map((s, idx) => s.day_of_week === dayIdx && (
                    <div key={idx} className="bg-white border border-stone-200 rounded p-1 text-[10px]">
                      <div className="flex items-center justify-between mb-0.5">
                        <select value={s.shift_code} onChange={(e) => updateShift(idx, 'shift_code', e.target.value)} className="text-[10px] bg-transparent font-semibold w-12 outline-none">
                          {systemRoles.map(r => <option key={r.code} value={r.code}>{r.code}</option>)}
                        </select>
                        <button onClick={() => removeShift(idx)} className="text-red-400 hover:text-red-600 text-xs">&times;</button>
                      </div>
                      <input type="time" value={s.start_time} onChange={(e) => updateShift(idx, 'start_time', e.target.value)} className="text-[10px] w-full bg-transparent outline-none" />
                      <input type="time" value={s.end_time} onChange={(e) => updateShift(idx, 'end_time', e.target.value)} className="text-[10px] w-full bg-transparent outline-none" />
                      <div className="flex gap-1 mt-0.5">
                        <input type="number" value={s.max_capacity} min="1" onChange={(e) => updateShift(idx, 'max_capacity', parseInt(e.target.value) || 1)} className="text-[10px] w-8 bg-transparent outline-none border-b border-stone-200" title="Capacity" />
                        <span className="text-stone-400">cap</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )); })()}
          </div>
          <Button onClick={() => handleUpdateTemplateWeek(tw.id, { name, shifts })} disabled={!name.trim()} className="w-full" data-testid="update-tw-submit-btn">
            Save Changes ({shifts.length} shifts &middot; {calcTemplateWeekHours(shifts)}h)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
